import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { NetworkConfig } from '../config/networks';
import { getTrustlineComplianceModuleAddress } from '../contracts/contract';
import './PolicyDescription.css';

interface PolicyDescriptionProps {
  currentNetwork?: NetworkConfig;
  contractAddress?: string;
  isConnected?: boolean;
  provider?: ethers.BrowserProvider | null;
}

const PolicyDescription: React.FC<PolicyDescriptionProps> = ({ currentNetwork, contractAddress, isConnected, provider }) => {
  // Compute email domain based on compliance module address
  const addressToDomain: Record<string, string> = {
    '0x7ecebafdba28fe894076f5bfb7f7305d84dbe150': 'trustline.id',
    '0x7d3ef72df214829e9979652ba6ddebdefed51ef2': 'tokeny.com',
    '0xe0a6e57a0cf33116da0835eec6a5baee1df99251': 'tokeny.com'
  };
  const [complianceModuleAddress, setComplianceModuleAddress] = useState<string | null>(null);
  const whitelistedEmailDomain = addressToDomain[complianceModuleAddress?.toLowerCase() || ''] || 'trustline.id';

  // Fetch compliance module address for current token
  useEffect(() => {
    const fetchComplianceModuleAddress = async () => {
      if (!provider || !contractAddress) {
        setComplianceModuleAddress(null);
        return;
      }

      try {
        const moduleAddress = await getTrustlineComplianceModuleAddress(provider, contractAddress);
        setComplianceModuleAddress(moduleAddress);
      } catch (error) {
        console.error('Failed to fetch compliance module address:', error);
        setComplianceModuleAddress(null);
      }
    };

    fetchComplianceModuleAddress();
  }, [provider, contractAddress]);
  
  return (
    <div className="policy-section">
      <div className="policy-header">
        <h3>Policy</h3>
        {currentNetwork && contractAddress && (
          <a 
            href={`${currentNetwork.blockExplorer}/address/${contractAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="explorer-link"
          >
            View contract on explorer ↗
          </a>
        )}
      </div>
      <div className="policy-content">
        <p className="policy-description">
          Transactions made on{' '}
          {currentNetwork && contractAddress ? (
            <a 
              href={`${currentNetwork.blockExplorer}/address/${contractAddress}`}
              target="_blank"
              rel="noopener noreferrer"
              className="contract-link"
            >
              this contract
            </a>
          ) : (
            'this contract'
          )}
          {' '}are subject to verifications through Trustline trusted providers.
        </p>
        {isConnected && complianceModuleAddress && (
          <div className="policy-items">
            <div className="policy-item">
              <div className="policy-item-header">
                <span className="policy-name">Watch Lists</span>
                <div className="policy-scopes">
                  <span className="policy-scope scope-sender">Sender</span>
                  <span className="policy-scope">Beneficiary</span>
                </div>
              </div>
              <ul className="policy-desc policy-list">
                <li>OFAC Sanctions list</li>
                <li>Community self-reported lists</li>
                <li>Service internal lists</li>
              </ul>
            </div>
            <div className="policy-item">
              <div className="policy-item-header">
                <span className="policy-name">End-User Verification</span>
                <span className="policy-scope">Beneficiary</span>
              </div>
              <span className="policy-desc">
              <ul className="policy-desc policy-list">
                <li>For all transfers, <strong>Email</strong> verification of the beneficiary, which needs to belong to: <strong>@{whitelistedEmailDomain}</strong></li>
                <li>For high amount transfers, <strong>Phone number</strong> verification of beneficary within: <strong>EU or US region</strong>.</li>
              </ul>
              </span>
            </div>
            <div className="policy-item">
              <div className="policy-item-header">
                <span className="policy-name">Fraud & Hack Prevention</span>
                <div className="policy-scopes">
                  <span className="policy-scope">Beneficiary</span>
                  <span className="policy-scope scope-transaction">Transaction</span>
                </div>
              </div>
              <ul className="policy-desc policy-list">
                <li><strong>Simulate tx impact</strong> on smart contract.</li>
                <li>Check <strong>risk score</strong> for tx wallets and assets.</li>
                <li>Require secondary beneficiary verification for <strong>high amount transfers.</strong></li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PolicyDescription;
